import {FC, useMemo, useState} from 'react'
import {useQuery, useQueryClient} from 'react-query'
import Swal from 'sweetalert2'
import {ID, getApiErrorMessage} from '../../../helpers'
import {
  ContractAiAnalysisStatusResponse,
  ContractRushmindEstimate,
  ContractRushmindProgress,
  ContractSuggestedItem,
} from '../../../../app/modules/contracts/components/contracts-list/core/_models'
import {
  createTasksFromContractSuggestions,
  getAiAnalysisStatus,
  getRushmindAnalysisProgressOrNull,
} from '../../../../app/modules/contracts/components/contracts-list/core/_requests'
import {invalidateContractsQueries} from '../../../../app/modules/contracts/components/contracts-list/core/invalidateContractsQueries'
import {Loading} from '../../components/loading/Loading'
import {Toast} from '../../widgets'
import {getContractAiStatusMessage} from './contractAiMessages'
import {ContractSuggestedItemsPreview} from './ContractSuggestedItemsPreview'

type Props = {
  contractId: ID
  enabled?: boolean
  initialEstimate?: ContractRushmindEstimate | null
  showAfterCreateIntro?: boolean
  /** Exibe a lista de sugestões no próprio painel ao concluir (ex.: modal permanece aberto) */
  showSuggestionsWhenComplete?: boolean
  onSuggestionsFinished?: () => void
}

type RushmindPhase = 'loading' | 'queued' | 'processing' | 'completed' | 'failed' | 'unknown'

function formatSuggestionCount(count: number): string {
  if (count === 1) {
    return '1 sugestão de tarefa disponível na listagem de contratos.'
  }
  return `${count} sugestões de tarefa disponíveis na listagem de contratos.`
}

function initializeSuggestedItems(items: ContractSuggestedItem[]): ContractSuggestedItem[] {
  return items.map((item) => ({
    ...item,
    is_task: true,
    is_phase: false,
  }))
}

function resolvePhase(
  progressLoading: boolean,
  statusLoading: boolean,
  progress: ContractRushmindProgress | undefined,
  analysisStatus: ContractAiAnalysisStatusResponse | undefined,
  needsStatusQuery: boolean
): RushmindPhase {
  if (progressLoading || (needsStatusQuery && statusLoading)) {
    return 'loading'
  }
  if (analysisStatus?.status === 'completed') {
    return 'completed'
  }
  if (analysisStatus?.status === 'failed') {
    return 'failed'
  }
  if (progress?.status === 'processing') {
    return 'processing'
  }
  if (progress?.status === 'queued') {
    return 'queued'
  }
  if (analysisStatus?.status === 'processing') {
    return 'processing'
  }
  if (analysisStatus?.status === 'queued') {
    return 'queued'
  }
  return 'unknown'
}

const ContractRushmindProgressPanel: FC<Props> = ({
  contractId,
  enabled = true,
  initialEstimate,
  showAfterCreateIntro = false,
  showSuggestionsWhenComplete = false,
  onSuggestionsFinished,
}) => {
  const queryClient = useQueryClient()
  const [isCreatingTasks, setIsCreatingTasks] = useState(false)

  const getSwalTheme = () => {
    const theme = window.localStorage.getItem('kt_theme_mode_value') || ''
    return theme === 'dark' ? '#353b48' : '#fff'
  }

  const {
    data: progressPayload,
    isLoading: progressLoading,
    isError: progressError,
    error: progressQueryError,
  } = useQuery(
    ['contractRushmindProgress', contractId],
    () => getRushmindAnalysisProgressOrNull(contractId),
    {
      enabled: enabled && !!contractId,
      refetchInterval: (data) => {
        const status = data?.data?.status
        return status === 'queued' || status === 'processing' ? 4000 : false
      },
      refetchOnWindowFocus: true,
    }
  )

  const progress = progressPayload?.data ?? undefined
  const needsStatusQuery = !progressLoading && !progress
  const pollAnalysisStatus = showSuggestionsWhenComplete || needsStatusQuery

  const {
    data: analysisStatus,
    isLoading: statusLoading,
    isError: statusError,
  } = useQuery(
    ['contractAiAnalysisStatus', contractId],
    () => getAiAnalysisStatus(contractId),
    {
      enabled: enabled && !!contractId && pollAnalysisStatus,
      refetchInterval: (data) => {
        const status = data?.status
        return status === 'queued' || status === 'processing' ? 4000 : false
      },
      refetchOnWindowFocus: true,
    }
  )

  const phase = resolvePhase(
    progressLoading,
    statusLoading,
    progress,
    analysisStatus,
    needsStatusQuery && !showSuggestionsWhenComplete
  )

  const result = analysisStatus?.result
  const suggestionItems = result?.suggestions?.items ?? []
  const suggestionCount = suggestionItems.length
  const hasSuggestions = suggestionCount > 0 && result?.status === 'success'
  const showInlineSuggestions =
    showSuggestionsWhenComplete && phase === 'completed' && hasSuggestions

  const activeEstimateLabel = useMemo(() => {
    if (progress?.estimated_wait_label) {
      return progress.estimated_wait_label
    }
    if (phase === 'queued' || phase === 'processing') {
      return initialEstimate?.estimated_wait_label
    }
    return undefined
  }, [progress, phase, initialEstimate])

  const handleConfirmSuggestions = async (selected: ContractSuggestedItem[]) => {
    if (!contractId || selected.length === 0) return

    const tasksPayload = selected.map((i) => ({
      title: i.title,
      description: i.description ?? null,
      expected_date: i.expected_date ?? null,
    }))

    setIsCreatingTasks(true)
    try {
      const r = await createTasksFromContractSuggestions(contractId, {tasks: tasksPayload})

      Swal.fire({
        icon: 'success',
        title: 'Tarefas criadas',
        html: `<h5>${r.message}</h5>`,
        background: getSwalTheme(),
        confirmButtonColor: '#009ef7',
        color: '#fff',
      })
      invalidateContractsQueries(queryClient)
      onSuggestionsFinished?.()
    } catch (ex) {
      Toast.fire({icon: 'error', title: getApiErrorMessage(ex)})
    } finally {
      setIsCreatingTasks(false)
    }
  }

  const handleDismissSuggestions = () => {
    onSuggestionsFinished?.()
  }

  const renderQueueEstimate = () => {
    if (!activeEstimateLabel) {
      return null
    }

    return (
      <li className='mb-0'>
        <span className='text-muted d-block fs-7'>Tempo médio estimado até concluir</span>
        <span className='fw-semibold text-gray-800'>{activeEstimateLabel}</span>
        {!progress &&
          initialEstimate &&
          initialEstimate.queue_ahead > 0 &&
          phase === 'queued' && (
            <span className='text-muted fs-7 d-block mt-1'>
              {initialEstimate.queue_ahead} documento
              {initialEstimate.queue_ahead > 1 ? 's' : ''} na fila antes do seu
            </span>
          )}
      </li>
    )
  }

  const renderBody = () => {
    if (phase === 'loading') {
      return (
        <div className='text-center py-6'>
          <Loading />
          <p className='text-muted mt-3 mb-0'>Consultando status da análise...</p>
        </div>
      )
    }

    if (progressError && statusError) {
      const message =
        (progressQueryError as {response?: {data?: {message?: string}}})?.response?.data?.message ??
        'Não foi possível obter o status da análise.'
      return <p className='text-muted mb-0'>{message}</p>
    }

    if (showInlineSuggestions) {
      const detail = getContractAiStatusMessage(result?.status ?? 'success', result?.message)

      return (
        <>
          <div className='d-flex align-items-start gap-3 mb-4'>
            <i className='bi bi-check-circle-fill fs-2x text-success flex-shrink-0' />
            <div>
              <p className='fw-bold mb-1 text-gray-900'>Análise concluída</p>
              <p className='text-gray-600 fs-7 mb-0'>{detail}</p>
            </div>
          </div>
          <ContractSuggestedItemsPreview
            items={initializeSuggestedItems(suggestionItems)}
            isCreating={isCreatingTasks}
            onConfirm={handleConfirmSuggestions}
            onCancel={handleDismissSuggestions}
          />
        </>
      )
    }

    if (phase === 'completed') {
      const detail = getContractAiStatusMessage(
        result?.status ?? 'success',
        result?.message
      )

      return (
        <div className='d-flex align-items-start gap-3'>
          <i className='bi bi-check-circle-fill fs-2x text-success' />
          <div>
            <p className='fw-bold mb-1 text-gray-900'>Contrato salvo — análise concluída</p>
            <p className='text-gray-700 fs-7 mb-2'>{detail}</p>
            {hasSuggestions ? (
              <p className='text-primary fs-7 fw-semibold mb-0'>
                {formatSuggestionCount(suggestionCount)}
              </p>
            ) : (
              <p className='text-muted fs-7 mb-0'>
                Você pode fechar este modal e consultar o contrato na listagem quando quiser.
              </p>
            )}
          </div>
        </div>
      )
    }

    if (phase === 'failed') {
      const detail =
        result?.message ??
        'Não foi possível concluir a análise. Tente novamente mais tarde ou consulte o contrato na listagem.'

      return (
        <div className='d-flex align-items-start gap-3'>
          <i className='bi bi-exclamation-triangle-fill fs-2x text-warning' />
          <div>
            <p className='fw-bold mb-1 text-gray-900'>Análise do RushMind encerrada</p>
            <p className='text-gray-700 fs-7 mb-0'>{detail}</p>
          </div>
        </div>
      )
    }

    if (phase === 'processing') {
      return (
        <>
          <div className='d-flex align-items-center gap-3 mb-5'>
            <span className='spinner-border text-primary' />
            <div>
              <p className='fw-bold mb-0 text-gray-800'>RushMind processando agora</p>
              <p className='text-muted fs-7 mb-0'>Seu documento está sendo analisado neste momento.</p>
            </div>
          </div>
          <ul className='list-unstyled mb-0 fs-6'>
            {renderQueueEstimate()}
            {progress && progress.queue_total > 1 && (
              <li className='mb-0 mt-3'>
                <span className='text-muted d-block fs-7'>Posição na fila geral</span>
                <span className='fw-semibold text-gray-800'>
                  {progress.queue_position}º de {progress.queue_total} em andamento
                </span>
              </li>
            )}
          </ul>
        </>
      )
    }

    if (phase === 'queued' || phase === 'unknown') {
      return (
        <>
          <div className='d-flex align-items-center gap-3 mb-5'>
            <span className='spinner-border spinner-border-sm text-info' />
            <div>
              <p className='fw-bold mb-0 text-gray-800'>Aguardando na fila</p>
              <p className='text-muted fs-7 mb-0'>
                O RushMind analisará seu contrato quando chegar a vez.
              </p>
            </div>
          </div>
          <ul className='list-unstyled mb-0 fs-6'>
            {progress && (
              <>
                <li className='mb-3'>
                  <span className='text-muted d-block fs-7'>Sua posição</span>
                  <span className='fw-semibold text-gray-800'>
                    {progress.queue_position}º na fila
                    {progress.queue_total > 0 && ` (de ${progress.queue_total} em andamento)`}
                  </span>
                </li>
                {progress.queue_ahead > 0 && (
                  <li className='mb-3'>
                    <span className='text-muted d-block fs-7'>À sua frente</span>
                    <span className='fw-semibold text-gray-800'>
                      {progress.queue_ahead} documento{progress.queue_ahead > 1 ? 's' : ''}
                    </span>
                  </li>
                )}
              </>
            )}
            {renderQueueEstimate()}
          </ul>
        </>
      )
    }

    return null
  }

  const renderIntro = () => {
    if (!showAfterCreateIntro || showInlineSuggestions) {
      return null
    }

    if (phase === 'completed') {
      return null
    }

    if (phase === 'failed') {
      return (
        <div className='alert alert-warning d-flex align-items-start gap-3 mb-5'>
          <i className='bi bi-exclamation-triangle-fill fs-2' />
          <div>
            <p className='fw-bold mb-1 text-gray-900'>Contrato salvo com sucesso!</p>
            <p className='text-gray-700 fs-7 mb-0'>
              O contrato foi salvo, mas a análise automática não pôde ser concluída.
            </p>
          </div>
        </div>
      )
    }

    return (
      <div className='alert alert-success d-flex align-items-start gap-3 mb-5'>
        <i className='bi bi-check-circle-fill fs-2' />
        <div>
          <p className='fw-bold mb-1 text-gray-900'>Contrato salvo com sucesso!</p>
          <p className='text-gray-700 fs-7 mb-0'>
            O RushMind está analisando o documento. Acompanhe o status abaixo.
          </p>
        </div>
      </div>
    )
  }

  const renderFooter = () => {
    if (showInlineSuggestions) {
      return null
    }

    if (phase === 'completed') {
      return (
        <p className='text-muted fs-7 mt-5 mb-0 border-top pt-4'>
          {hasSuggestions ? (
            <>
              Feche este modal e, na listagem de contratos, use o menu ou o ícone do RushMind no
              contrato recém-criado para revisar as sugestões.
            </>
          ) : (
            <>
              Você pode fechar este modal. O contrato já está salvo e a análise foi concluída.
            </>
          )}
        </p>
      )
    }

    if (phase === 'failed') {
      return (
        <p className='text-muted fs-7 mt-5 mb-0 border-top pt-4'>
          Você pode fechar este modal. O contrato foi salvo; consulte a listagem para mais detalhes.
        </p>
      )
    }

    if (phase === 'loading') {
      return null
    }

    return (
      <p className='text-muted fs-7 mt-5 mb-0 border-top pt-4'>
        Você pode fechar este modal e o carregamento continua em segundo plano. Você será
        notificado quando a análise terminar. Para acompanhar manualmente, acesse a listagem de
        contratos e clique no <strong>ícone do RushMind</strong> no contrato.
      </p>
    )
  }

  return (
    <div>
      {renderIntro()}
      {renderBody()}
      {renderFooter()}
    </div>
  )
}

export {ContractRushmindProgressPanel}
